/* Prestige 5.7.0 extension. Shopify/Prestige still apply filters, sorting and AJAX updates. */
(() => {
  'use strict';
  const instances = new WeakMap();
  const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('de').replace(/ß/g, 'ss').trim();

  function init(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const listen = (element, event, fn, options = {}) => element.addEventListener(event, fn, { ...options, signal });
    const one = selector => root.querySelector(selector);
    const all = selector => Array.from(root.querySelectorAll(selector));
    const drawer = one('.catalog-drawer');
    const panel = one('.catalog-featured-panel');
    const desktop = window.matchMedia('(min-width: 1008px)');
    const queries = new Map();
    const opened = new Map();
    let activeKey = null;
    let featuredTrigger = null;
    let returnFocus = null;
    let waiting = false;
    let timer = null;
    let oldState = one('[data-catalog-state]');
    let savedFocus = null;
    let drawerScroll = 0;
    let panelScroll = 0;
    let disposed = false;
    let rebuilding = false;

    const groupFor = (form, key) => form && Array.from(form.querySelectorAll('[data-filter-key]')).find(group => group.dataset.filterKey === key);
    const canonicalForm = () => one('#collection-filters-drawer-form');
    const countText = count => `${count} ${count === 1 ? 'Produkt' : 'Produkte'}`;

    function searchGroup(group) {
      const search = group.querySelector('[data-filter-search]');
      const query = normalize(search ? search.value : '');
      let shown = 0;
      group.querySelectorAll('[data-filter-row]').forEach(row => {
        row.hidden = !normalize(row.dataset.label || '').includes(query);
        if (!row.hidden) shown++;
      });
      const empty = group.querySelector('.catalog-filter__no-match');
      if (empty) empty.hidden = shown > 0;
    }

    function remember() {
      all('form').forEach(form => {
        form.querySelectorAll('[data-filter-key]').forEach(group => {
          if (form.dataset.featuredForm) return;
          opened.set(`${form.id}|${group.dataset.filterKey}`, group.open);
        });
      });
      drawerScroll = drawer.querySelector('.Drawer__Main').scrollTop;
      panelScroll = panel.scrollTop;
      const focus = document.activeElement;
      if (root.contains(focus)) {
        savedFocus = {
          form: focus.closest('form')?.id,
          key: focus.closest('[data-filter-key]')?.dataset.filterKey,
          name: focus.getAttribute('name'), value: focus.getAttribute('value'),
          search: focus.hasAttribute('data-filter-search'),
          remove: focus.matches('[data-catalog-remove]')
        };
      }
    }

    function restoreForms() {
      all('form [data-filter-key]').forEach(group => {
        const id = group.closest('form').id;
        const openKey = `${id}|${group.dataset.filterKey}`;
        if (opened.has(openKey)) group.open = opened.get(openKey);
        const input = group.querySelector('[data-filter-search]');
        if (input) input.value = queries.get(group.dataset.filterKey) || '';
        searchGroup(group);
      });
    }

    function closeFeatured(focus = false) {
      panel.hidden = true;
      panel.querySelector('[data-featured-content]').replaceChildren();
      if (featuredTrigger) featuredTrigger.setAttribute('aria-expanded', 'false');
      activeKey = null;
      if (focus && featuredTrigger?.isConnected) featuredTrigger.focus({ preventScroll: true });
    }

    function positionFeatured() {
      if (panel.hidden || !featuredTrigger) return;
      const rootRect = root.getBoundingClientRect();
      const rect = featuredTrigger.getBoundingClientRect();
      const available = Math.max(180, window.innerHeight - rect.bottom - 20);
      panel.style.left = `${Math.max(0, Math.min(rect.left - rootRect.left, root.clientWidth - panel.offsetWidth))}px`;
      panel.style.top = `${rect.bottom - rootRect.top + 8}px`;
      panel.style.maxHeight = `${Math.min(480, available)}px`;
    }

    function rebuildFeatured(focus = false) {
      if (!activeKey) return;
      const source = canonicalForm();
      if (!groupFor(source, activeKey)) { closeFeatured(focus); return; }
      const form = source.cloneNode(true);
      form.id = 'catalog-featured-form';
      form.dataset.featuredForm = 'true';
      // Full form state preserves filters from other groups; only one group is visible.
      form.querySelectorAll('[id]').forEach(el => { el.id = `featured-${el.id}`; });
      form.querySelectorAll('[for]').forEach(el => { el.htmlFor = `featured-${el.htmlFor}`; });
      form.querySelectorAll('[data-filter-key]').forEach(group => {
        group.hidden = group.dataset.filterKey !== activeKey;
        group.open = true;
        const input = group.querySelector('[data-filter-search]');
        if (input) input.value = queries.get(group.dataset.filterKey) || '';
        searchGroup(group);
      });
      panel.querySelector('[data-featured-content]').replaceChildren(form);
      panel.hidden = false;
      positionFeatured();
      panel.scrollTop = panelScroll;
      if (focus) {
        const group = groupFor(form, activeKey);
        (group.querySelector('[data-filter-search]') || group.querySelector('input:not([disabled])') || group.querySelector('summary')).focus({ preventScroll: true });
      }
    }

    function sync() {
      if (disposed || rebuilding) return;
      rebuilding = true;
      const state = one('[data-catalog-state]');
      if (!state) { rebuilding = false; return; }
      const count = Number(state.dataset.count);
      const active = Number(state.dataset.activeCount);
      const hasFilters = Number(state.dataset.visibleCount) > 0;
      all('[data-count-label]').forEach(el => { el.textContent = countText(count); });
      all('[data-sort-label]').forEach(el => { el.textContent = state.dataset.sortLabel; });
      all('[data-catalog-reset]').forEach(el => {
        const url = new URL(root.dataset.collectionUrl, location.origin);
        url.searchParams.set('sort_by', state.dataset.sort);
        el.href = url.toString(); el.hidden = active === 0;
      });
      one('[data-results-button]').textContent = `${countText(count)} anzeigen`;
      one('.catalog-sidebar').hidden = !hasFilters;
      root.classList.toggle('catalog--no-filters', !hasFilters);
      one('.catalog-toolbar__filter').hidden = !hasFilters && active === 0;
      all('[data-featured-filter]').forEach(button => {
        const group = groupFor(canonicalForm(), button.dataset.featuredFilter);
        button.hidden = !group;
        if (!group) return;
        const activeLabels = group.dataset.activeLabels;
        const label = button.dataset.filterLabel;
        button.querySelector('[data-featured-label]').textContent = activeLabels ? `${label}: ${activeLabels}` : `${label} wählen`;
        button.title = activeLabels ? `${label}: ${activeLabels}` : `${label} wählen`;
        button.classList.toggle('is-active', Boolean(activeLabels));
      });
      one('[data-featured-filters]').hidden = !all('[data-featured-filter]').some(el => !el.hidden);
      all('.catalog-sort [data-value]').forEach(button => {
        const selected = button.dataset.value === state.dataset.sort;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const chips = one('.CollectionInner__Products [data-active-filters]');
      const target = one('[data-drawer-active]');
      target.replaceChildren();
      if (chips && active > 0) target.append(chips.cloneNode(true));
      restoreForms();
      if (activeKey && desktop.matches) rebuildFeatured();
      rebuilding = false;
    }

    function beginLoading() {
      remember();
      oldState = one('[data-catalog-state]');
      waiting = true;
      clearTimeout(timer);
      root.classList.add('catalog--loading');
      one('.CollectionInner__Products').setAttribute('aria-busy', 'true');
      const button = one('[data-results-button]');
      button.textContent = 'Wird aktualisiert …';
      button.disabled = true;
      one('[data-catalog-error]').hidden = true;
      timer = setTimeout(() => {
        if (!waiting) return;
        // Do not invent results when native AJAX fails. The URL already contains the requested state.
        const error = one('[data-catalog-error]');
        error.hidden = false;
        one('[data-catalog-retry]').href = location.href;
        one('[data-catalog-status]').textContent = 'Die Auswahl konnte nicht aktualisiert werden. Bitte lade die Auswahl neu.';
        button.disabled = false;
        button.textContent = 'Auswahl neu laden';
        root.classList.remove('catalog--loading');
      }, 15000);
    }

    function finishLoading() {
      const state = one('[data-catalog-state]');
      if (!waiting || state === oldState) return; // Ignore loading events from other theme components.
      waiting = false;
      clearTimeout(timer);
      root.classList.remove('catalog--loading');
      one('.CollectionInner__Products').removeAttribute('aria-busy');
      one('[data-results-button]').disabled = false;
      one('[data-catalog-error]').hidden = true;
      sync();
      one('[data-catalog-status]').textContent = `${countText(Number(state.dataset.count))} gefunden.`;
      // Restore focus after Prestige replaces the filter forms; avoid jumping the document.
      if (savedFocus?.form && savedFocus.key) {
        const form = all('form').find(el => el.id === savedFocus.form);
        const group = groupFor(form, savedFocus.key);
        const input = savedFocus.search ? group?.querySelector('[data-filter-search]') :
          Array.from(group?.querySelectorAll('input') || []).find(el => el.name === savedFocus.name && el.value === savedFocus.value);
        if (input && !input.disabled) input.focus({ preventScroll: true });
        else if (group && !group.hidden) group.querySelector('summary')?.focus({ preventScroll: true });
      } else if (savedFocus?.remove) {
        const area = drawer.getAttribute('aria-hidden') === 'false' ? one('[data-drawer-active]') : one('.CollectionInner__Products');
        const next = area.querySelector('[data-catalog-remove]');
        if (next) next.focus({ preventScroll: true });
        else if (drawer.getAttribute('aria-hidden') === 'false') one('[data-results-button]').focus({ preventScroll: true });
        else {
          const products = one('.CollectionInner__Products');
          products.tabIndex = -1;
          products.focus({ preventScroll: true });
        }
      }
      drawer.querySelector('.Drawer__Main').scrollTop = drawerScroll;
      panel.scrollTop = panelScroll;
      savedFocus = null;
    }

    listen(root, 'input', event => {
      if (!event.target.matches('[data-filter-search]')) return;
      const group = event.target.closest('[data-filter-key]');
      queries.set(group.dataset.filterKey, event.target.value);
      searchGroup(group);
    });
    listen(root, 'change', event => {
      if (!event.target.name?.startsWith('filter.')) return;
      if (waiting) { event.stopImmediatePropagation(); event.target.checked = !event.target.checked; return; }
      beginLoading();
    }, { capture: true });
    listen(root, 'click', event => {
      const trigger = event.target.closest('[data-featured-filter]');
      if (trigger) {
        event.preventDefault();
        if (waiting) return;
        const key = trigger.dataset.featuredFilter;
        if (!desktop.matches) {
          closeFeatured(); returnFocus = trigger;
          const group = groupFor(canonicalForm(), key);
          if (group) {
            group.open = true;
            queries.delete(key);
            const search = group.querySelector('[data-filter-search]');
            if (search) search.value = '';
            searchGroup(group);
          }
          one('[data-mobile-filter-opener]').click();
          if (group) requestAnimationFrame(() => {
            const body = drawer.querySelector('.Drawer__Main');
            body.scrollTop += group.getBoundingClientRect().top - body.getBoundingClientRect().top;
            group.querySelector('summary').focus({ preventScroll: true });
          });
        } else if (activeKey === key && !panel.hidden) closeFeatured(true);
        else {
          closeFeatured(); featuredTrigger = trigger; activeKey = key; panelScroll = 0;
          trigger.setAttribute('aria-expanded', 'true'); rebuildFeatured(true);
        }
        return;
      }
      if (event.target.closest('[data-close-featured]')) { closeFeatured(true); return; }
      const remove = event.target.closest('[data-catalog-remove]');
      if (remove && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        event.preventDefault(); event.stopPropagation();
        if (waiting) return;
        beginLoading();
        // Reuse Prestige's native clear/remove request path without nested click targets.
        const nativeTrigger = document.createElement('button');
        nativeTrigger.hidden = true;
        nativeTrigger.type = 'button';
        nativeTrigger.dataset.action = 'clear-filters';
        nativeTrigger.dataset.url = remove.href;
        root.append(nativeTrigger); nativeTrigger.click(); nativeTrigger.remove();
        return;
      }
      if (event.target.closest('[data-results-button]') && waiting && !one('[data-catalog-error]').hidden) {
        event.preventDefault(); event.stopPropagation(); location.assign(location.href); return;
      }
      if (event.target.closest('.catalog-sort [data-action="select-value"]')) {
        if (waiting) { event.preventDefault(); event.stopPropagation(); return; }
        beginLoading();
      }
      const opener = event.target.closest('.catalog-toolbar__filter');
      if (opener) {
        returnFocus = opener;
        closeFeatured();
        canonicalForm().querySelectorAll('[data-filter-key]').forEach(group => {
          group.open = false;
          opened.set(`${canonicalForm().id}|${group.dataset.filterKey}`, false);
        });
        drawer.querySelector('.Drawer__Main').scrollTop = 0;
      }
      if (event.target.closest('.catalog-toolbar__sort')) closeFeatured();
    }, { capture: true });
    listen(root, 'submit', event => {
      if (event.target.matches('.collection-filters-form')) event.preventDefault(); // Search Enter must not submit the form.
    });
    listen(document, 'click', event => {
      if (!panel.hidden && !panel.contains(event.target) && !event.target.closest('[data-featured-filter]')) closeFeatured();
    });
    listen(document, 'keydown', event => {
      if (event.key === 'Escape' && !panel.hidden) { event.preventDefault(); closeFeatured(true); }
    });
    listen(document, 'theme:loading:end', finishLoading);
    // Native back/forward uses full-page restoration; cover null history states safely.
    listen(window, 'popstate', () => { if (root.isConnected) location.reload(); });
    listen(window, 'resize', () => {
      if (!desktop.matches) closeFeatured();
      else positionFeatured();
      viewport();
    });
    function viewport() {
      const height = window.visualViewport?.height || window.innerHeight;
      const offset = window.visualViewport?.offsetTop || 0;
      root.style.setProperty('--catalog-viewport-height', `${height}px`);
      root.style.setProperty('--catalog-viewport-top', `${offset}px`);
    }
    if (window.visualViewport) {
      listen(window.visualViewport, 'resize', viewport);
      listen(window.visualViewport, 'scroll', viewport);
    }
    const drawerObserver = new MutationObserver(() => {
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';
      one('.catalog-toolbar__filter').setAttribute('aria-expanded', String(isOpen));
      if (!desktop.matches && returnFocus?.hasAttribute('data-featured-filter')) returnFocus.setAttribute('aria-expanded', String(isOpen));
      if (!isOpen && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    });
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    viewport(); sync();
    instances.set(root, () => { disposed = true; clearTimeout(timer); controller.abort(); drawerObserver.disconnect(); instances.delete(root); });
  }
  const boot = () => document.querySelectorAll('[data-catalog]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-catalog]').forEach(root => instances.get(root)?.());
  });
})();
